/**
 * Decision values as the controls read and write them. A catalog entry's `options` are keyed by sub-field ("" = the decision itself,
 * "front" = a field of a composite decision); a setting's path is `<decisionId>` or `<decisionId>.<sub>`, and a decision edit is a
 * `{ path: value }` patch (patchDraft `decisions`), one entry per sub-field that changed.
 */
import { decisionName, DecisionId, STANDING_POLICY, type DecisionFamily, type Decisions } from "@bsim/domain";
import type { DecisionCatalogEntry } from "@bsim/contracts";

export type SettingValue = number | string | boolean | null;
export type SettingOption = DecisionCatalogEntry["options"][string][number];
/** What `patchDraft` takes for a decision edit: leaf path to its new value. */
export type DecisionPatch = Readonly<Record<string, SettingValue>>;

/** The leaf path of one sub-field of a decision ("" = the decision itself). */
export const settingPath = (id: string, sub: string): string => (sub === "" ? id : `${id}.${sub}`);

/** "deposits.retailMm.rate.front" → "deposits.retailMm.rate": the decision a path (or an id) belongs to; the path itself when it names none. */
export const decisionIdOf = (path: string): string => DecisionId.options.find((d) => path === d || path.startsWith(`${d}.`)) ?? path;

/** The sub-field of `path` within decision `id` ("" for the decision itself); null when the path belongs to another decision. */
export const subOf = (path: string, id: string): string | null => (path === id ? "" : path.startsWith(`${id}.`) ? path.slice(id.length + 1) : null);

export function readDecision(decisions: Partial<Decisions>, id: string, sub: string): SettingValue | undefined {
  let node: unknown = decisions;
  for (const key of settingPath(id, sub).split(".")) {
    if (typeof node !== "object" || node === null) return undefined;
    node = (node as Record<string, unknown>)[key];
  }
  return node as SettingValue | undefined;
}

/** The patch that sets one sub-field. */
export const writeDecision = (id: string, sub: string, value: SettingValue): DecisionPatch => ({ [settingPath(id, sub)]: value });

/** Grid values compare with a tolerance (rates and dollars are decimals). */
export const sameValue = (a: SettingValue | undefined, b: SettingValue | undefined): boolean =>
  typeof a === "number" && typeof b === "number" ? Math.abs(a - b) < 1e-9 : a === b;

export const optionLabel = (options: readonly SettingOption[], value: SettingValue): string =>
  options.find((o) => sameValue(o.value, value))?.label ?? String(value);

/** "Follow → Lag" over every sub-field that moved from last year's value; null when nothing did or last year's value is unknown. */
export function changeTag(entry: DecisionCatalogEntry, now: Decisions, was: Partial<Decisions>): string | null {
  const moved = Object.entries(entry.options).flatMap(([sub, options]) => {
    const before = readDecision(was, entry.decisionId, sub);
    const after = readDecision(now, entry.decisionId, sub);
    return before === undefined || after === undefined || sameValue(before, after) ? [] : [`${optionLabel(options, before)} → ${optionLabel(options, after)}`];
  });
  return moved.length === 0 ? null : moved.join(" · ");
}

/** A sub-decision's own name when the catalog gives none ("existingBase" → "Existing base"). */
/** A setting key said in plain words: "cet1TargetRate" becomes "CET1 target rate" (the catalog's own caption wins where it has one). */
export const subWords = (sub: string): string => {
  const said = sub.replace(/([A-Z])/g, " $1").toLowerCase().replace(/\bcet1\b/g, "CET1");
  return said.replace(/^./, (c) => c.toUpperCase());
};

/** Each moved leg of a decision with its own name, so a review line says which one changed ("Existing base": Follow → Lag); a one-leg decision has no leg name. */
export function changeLegs(entry: DecisionCatalogEntry, now: Decisions, was: Partial<Decisions>): readonly { readonly leg: string | null; readonly sub: string; readonly change: string }[] {
  const legs = Object.entries(entry.options);
  return legs.flatMap(([sub, options]) => {
    const before = readDecision(was, entry.decisionId, sub);
    const after = readDecision(now, entry.decisionId, sub);
    if (before === undefined || after === undefined || sameValue(before, after)) return [];
    return [{ sub, leg: legs.length === 1 ? null : (entry.subCaptions?.[sub] ?? subWords(sub)), change: `${optionLabel(options, before)} → ${optionLabel(options, after)}` }];
  });
}

/** The decisions of a family whose value differs from last year's (all of them when last year's is unknown). */
export const changedDecisions = (entries: readonly DecisionCatalogEntry[], family: DecisionFamily, now: Decisions, was: Partial<Decisions>): DecisionCatalogEntry[] => {
  const inFamily = entries.filter((e) => e.family === family);
  const moved = inFamily.filter((e) => changeTag(e, now, was) !== null);
  return moved.length > 0 ? moved : inFamily;
};

/** One name for a decision everywhere it is written: its catalog label ("Buyback target"); the id is plumbing and never shown. */
export const decisionTitle = (decisions: readonly Pick<DecisionCatalogEntry, "decisionId" | "label">[], id: DecisionId): string =>
  decisions.find((d) => d.decisionId === id)?.label ?? decisionName(id);

/** A decision's label without its trailing note ("Commercial property loan growth (relationship managers)" → "CRE growth"): the short name a chip or a sentence uses. */
export const shortLabel = (label: string): string => label.replace(/\s*\([^)]*\)\s*$/, "");

/** What a one-round decision's standing setting is called ("Hold dividend"), or null when the catalog has no single standing option. */
function standingLabel(entry: DecisionCatalogEntry): string | null {
  const subs = Object.entries(entry.options);
  const [only] = subs;
  const standing = subs.length === 1 && only !== undefined ? readDecision(STANDING_POLICY, entry.decisionId, only[0]) : undefined;
  return standing === undefined || only === undefined ? null : optionLabel(only[1], standing);
}

/** A one-round decision's change lasts the year in the setting; whether its effect lasts is the decision's own: a dividend step or a raise leaves a lasting level, an expense cut reverts. */
export function oneRoundLine(entry: DecisionCatalogEntry): string {
  const usual = standingLabel(entry) ?? "its standing setting";
  if (entry.decisionId === "capital.dividend") return `A step this year only: next year starts at ${usual}, measured from the new dividend`;
  if (entry.decisionId === "capital.issuance") return `A raise this year only: next year starts at ${usual}; the new shares stay`;
  return `This year only: returns to ${usual} next year`;
}

/** The words under an unchanged one-round control: the same claim as `oneRoundLine`, said before any change. */
export function oneRoundSame(entry: DecisionCatalogEntry, nowLabel: string | null): string {
  const usual = nowLabel ?? "its default";
  if (entry.decisionId === "capital.dividend") return `A step for one year: each year starts at ${usual}, measured from the current dividend`;
  if (entry.decisionId === "capital.issuance") return `A raise for one year: each year starts at ${usual}; shares already issued stay`;
  return `One year only: back to ${usual}`;
}
